import { useQuery } from "react-query";

import { getTargetSessionMetrics } from "api/targets";
import { GET_TARGET_SESSION_METRICS, STALE_TIME_1_MINUTES } from "@constants/reactQueryKeys";

/**
 * Fetches a target's L4 derived session metrics (the data behind TargetDetail
 * charts). Exposes `primitiveType` to branch chart selection, `metrics` as the
 * oldest-first series, `behaviorFlags` (server-derived mastery/regression
 * signals) for the detail banner, and the server-computed `trialsToCriterion`
 * / `trend` / `visualAnalysis` insights. Disabled for invalid (falsy/NaN)
 * target ids.
 */
const useGetTargetSessionMetrics = (targetId: number) => {
  const {
    data: response,
    isLoading,
    error,
    ...rest
  } = useQuery([GET_TARGET_SESSION_METRICS, targetId], () => getTargetSessionMetrics(targetId), {
    enabled: !!targetId,
    staleTime: STALE_TIME_1_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    primitiveType: response?.data?.primitive_type ?? null,
    sessionMetrics: response?.data?.metrics ?? [],
    stepAcquisitionSequence: response?.data?.step_acquisition_sequence ?? [],
    behaviorFlags: response?.data?.behavior_flags ?? null,
    trialsToCriterion: response?.data?.trials_to_criterion ?? null,
    trend: response?.data?.trend ?? null,
    visualAnalysis: response?.data?.visual_analysis ?? null,
    isLoadingSessionMetrics: isLoading,
    sessionMetricsError: error,
    ...rest,
  };
};

export default useGetTargetSessionMetrics;
